import type { LucideIcon } from 'lucide-react';

import {
  PER_TYPE_LIMIT,
  type SearchHit,
  type SearchResponse,
  type SearchType,
} from './contractsMirror.js';

/** Rows shown per type before the group offers the rest. */
export const PER_GROUP = 5;

/** A destination the palette can jump to, already filtered by the nav's own gates. */
export interface PaletteDestination {
  to: string;
  label: string;
  icon: LucideIcon;
}

export type PaletteRow =
  | { kind: 'hit'; key: string; hit: SearchHit }
  | { kind: 'more'; key: string; type: SearchType; remaining: number }
  | { kind: 'destination'; key: string; destination: PaletteDestination };

export interface PaletteGroup {
  key: string;
  /** Resolved heading text. */
  label: string;
  /** What the heading counts. `capped` means "and more beyond this". */
  count: number;
  capped: boolean;
  rows: PaletteRow[];
}

export interface BuildGroupsInput {
  response: SearchResponse | undefined;
  /** The types this session may search, already narrowed by the client gate. */
  availableTypes: SearchType[];
  activeType: SearchType | 'all';
  expanded: ReadonlySet<SearchType>;
  /** Heading text per type, resolved by the caller so this stays free of i18n. */
  labelForType: (type: SearchType) => string;
  destinations: PaletteDestination[];
  destinationsLabel: string;
}

/**
 * Turn one search response into the groups the panel draws and the flat row list
 * the keyboard walks.
 *
 * TWO RULES DECIDE EVERYTHING HERE.
 *
 * Order is fixed by `availableTypes`, never by how many rows each type returned.
 * A list that re-sorts itself as you type moves the row you were about to press.
 *
 * A count is what the SERVER found, not what is on screen. The endpoint fetches
 * `PER_TYPE_LIMIT + 1` rows per type so a full page can be told from a page that
 * happens to fit, so a count above the limit means "and more" — reporting 21
 * would be a number the palette cannot back up.
 */
export function buildGroups({
  response,
  availableTypes,
  activeType,
  expanded,
  labelForType,
  destinations,
  destinationsLabel,
}: BuildGroupsInput): { groups: PaletteGroup[]; rows: PaletteRow[] } {
  const hits = response?.hits ?? [];
  const counts = response?.counts ?? {};
  const types =
    activeType === 'all' ? availableTypes : availableTypes.filter((t) => t === activeType);

  const groups: PaletteGroup[] = [];

  for (const type of types) {
    const ofType = hits.filter((hit) => hit.type === type);
    if (!ofType.length) continue;

    const total = counts[type] ?? ofType.length;
    // Only the "everything" view caps a group; scoping to one type is the way to
    // ask for all of it, so the cap would be arguing with the tab you just chose.
    const capping = activeType === 'all' && !expanded.has(type) && ofType.length > PER_GROUP;
    const visible = capping ? ofType.slice(0, PER_GROUP) : ofType;

    const rows: PaletteRow[] = visible.map((hit) => ({
      kind: 'hit',
      key: `${hit.type}-${hit.id}`,
      hit,
    }));
    if (capping) {
      rows.push({
        kind: 'more',
        key: `more-${type}`,
        type,
        remaining: ofType.length - PER_GROUP,
      });
    }

    groups.push({
      key: type,
      label: labelForType(type),
      count: Math.min(total, PER_TYPE_LIMIT),
      capped: total > PER_TYPE_LIMIT,
      rows,
    });
  }

  // Destinations are a different kind of answer — a screen rather than a record —
  // so they come last and only when nothing has been scoped to one record type.
  if (activeType === 'all' && destinations.length) {
    groups.push({
      key: 'destinations',
      label: destinationsLabel,
      count: destinations.length,
      capped: false,
      rows: destinations.map((destination) => ({
        kind: 'destination',
        key: `to-${destination.to}`,
        destination,
      })),
    });
  }

  return { groups, rows: groups.flatMap((group) => group.rows) };
}
